import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import result from 'lodash/result';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import classNames from 'classnames';
import Label from 'label-component';
import Icon from 'icon-component';
import FormElementError from 'form-element-error-component';

class Select extends Component {
    constructor(props) {
        super(props);

        const {
            value,
            defaultValue
        } = props;

        this._controlled = (isUndefined(defaultValue));
        this._optionChange = this._optionChange.bind(this);

        this.state = {
            showError: false
        };

        if (this._controlled) {
            this.state.value = value; // eslint-disable-line react/no-direct-mutation-state
        }

        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentDidMount() {
        this._updateFormBinding();
    }

    componentWillReceiveProps(nextProps) {
        const nextState = {
            showError: nextProps.forceShowError
        };

        if (this._controlled && nextProps.value && nextProps.value !== this.state.value) {
            nextState.value = nextProps.value;
        }

        this.setState(nextState);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onChange,
            items,
            onErrorUpdated,
            error
        } = this.props;
        const {
            showError
        } = this.state;

        if (this._controlled && onChange && this.state.value !== prevState.value) {
            const {
                value
            } = this.state;

            onChange({
                value: value,
                label: result(find(items, {value: value}), 'label')
            });
        }

        this._updateFormBinding();

        if (onErrorUpdated) {
            const updatedError = (showError && error) ? error : null;

            onErrorUpdated(updatedError);
        }
    }

    _updateFormBinding() {
        const {
            formBinding,
            name
        } = this.props;
        const value = (this._controlled) ? this.state.value : this.refs.select.value;

        if (formBinding) {
            formBinding(name, value);
        }
    }

    _optionChange(evt) {
        const {
            onChange,
            items
        } = this.props;
        const {
            target: {
                value
            }
        } = evt;

        if (!this._controlled && onChange) {
            this._updateFormBinding();

            onChange({
                value: value,
                label: result(find(items, {value: value}), 'label')
            });

            return;
        }

        this.setState({
            value: value
        });
    }

    render() {
        const {
            className,
            name,
            items,
            disabled,
            loading,
            error,
            onErrorUpdated,
            helpText,
            label,
            icon,
            size,
            required,
            inline,
            defaultValue,
            placeholder,
            onIconClick,
            dataTestId
        } = this.props;
        const {
            showError,
            value
        } = this.state;
        const displayError = error && showError;
        const containerClasses = classNames(
            'Select',
            'FormElement',
            {[`FormElement-${size}`]: size},
            {'Select-selected': value},
            {'FormElement-optional': !required},
            {'FormElement-loading': loading},
            {'FormElement-error': displayError},
            {'FormElement-inline': inline},
            {'FormElement-with-icon': icon},
            className
        );
        const initialValue = (placeholder && !value) ? '' : defaultValue;
        const valueProp = (this._controlled)
            ? {value}
            : {initialValue};
        let itemValue;
        let itemLabel;

        return (
            <div className={containerClasses}>
                {label && <Label required={required}>{label}</Label>}
                <div className="FormElement-control">
                    <select
                        ref="select"
                        className="Select-select"
                        name={name}
                        disabled={disabled}
                        onChange={this._optionChange}
                        data-testid={dataTestId || 'Select-select-default'}
                        {...valueProp}
                    >
                        {placeholder &&
                            <option
                                disabled
                                value=""
                            >
                                {placeholder}
                            </option>
                        }
                        {items.map((item, i) => {
                            itemValue = item ? item.value : '';
                            itemLabel = item ? item.label : '';

                            return (
                                <option
                                    key={i}
                                    value={itemValue}
                                >
                                    {itemLabel}
                                </option>
                            );
                        })}
                    </select>
                    {icon &&
                        <Icon
                            name={icon}
                            onClick={onIconClick}
                        />
                    }
                </div>
                {helpText && <span className="FormElement-help-text">{helpText}</span>}
                {displayError && !onErrorUpdated && <FormElementError error={error} />}
            </div>
        );
    }
}

Select.propTypes = {
    className: PropTypes.string,
    /** A unique name which is used to identify this element. If belonging to a form this is used as the key for validation bindings and is required. */
    name: PropTypes.string,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    label: PropTypes.string,
    icon: PropTypes.string,
    onIconClick: PropTypes.func,
    items: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ])
    })).isRequired,
    placeholder: PropTypes.string,
    size: PropTypes.oneOf(['lg', 'md']),
    helpText: PropTypes.string,
    inline: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    onErrorUpdated: PropTypes.func,
    required: PropTypes.bool,
    dataTestId: PropTypes.string,
    /** The id of the form that this element belongs to. If set it will be tracked by the form internally and not used in the component itself. */
    formId: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    /** Do not set the below outright, they are passed in from the Form or FormElement component. */
    formBinding: PropTypes.func,
    forceShowError: PropTypes.bool,
    error: PropTypes.string
};

Select.defaultProps = {
    className: '',
    value: '',
    disabled: false,
    loading: false,
    inline: false
};

export default Select;



// WEBPACK FOOTER //
// ./frontend/src/js/components/select/Select.jsx